/**
 * Modal de **alta de rollos** (galv por kg / acero-aluminio por metros).
 *
 * Réplica del "agregar lote/batch" del SSR (`inventario.html`), pero sobre la API
 * REST: el tipo (galv/acero) decide los campos. Galv pide calibre + formato + kg;
 * acero pide perfil + metros. Valida con Zod (`addGalvSchema`/`addAceroSchema`,
 * `safeParse`) antes de llamar a `POST /inventario/rollos` (vía `useAddRollo`). La
 * sede es la activa (la pone `lib/api`). El error de la API se muestra en el
 * diálogo. Presentacional + hooks; sin `fetch`.
 */
import { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Input,
} from "@/components/ui";

import {
  CALIBRES_GALV,
  FORMATOS_GALV,
  PERFILES_ACERO,
} from "../constants";
import { inventarioErrorMessage } from "../format";
import { useAddRollo } from "../hooks/useInventarioMutations";
import { addAceroSchema, addGalvSchema } from "../schemas";
import type { AddRolloBody, RolloTipo } from "../types";

interface AddRolloDialogProps {
  open: boolean;
  /** Tipo inicial sugerido (la pestaña activa de la pantalla). */
  initialTipo?: RolloTipo;
  onClose: () => void;
}

/** Errores por campo (de Zod), unificados para ambos tipos. */
type FieldErrors = Partial<
  Record<"calibre" | "fmt" | "kg" | "perfil_id" | "metros", string>
>;

export function AddRolloDialog({ open, initialTipo = "galv", onClose }: AddRolloDialogProps) {
  const addRollo = useAddRollo();

  const [tipo, setTipo] = useState<RolloTipo>(initialTipo);
  const [calibre, setCalibre] = useState("");
  const [fmt, setFmt] = useState("");
  const [perfilId, setPerfilId] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [nota, setNota] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // Al (re)abrir, parte del tipo sugerido y limpia el formulario y el error API.
  useEffect(() => {
    if (open) {
      setTipo(initialTipo);
      setCalibre("");
      setFmt("");
      setPerfilId("");
      setCantidad("");
      setNota("");
      setFieldErrors({});
      addRollo.reset();
    }
    // Solo nos interesa reaccionar a la apertura (la mutación es estable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialTipo]);

  /** Reúne el primer error de cada campo, omitiendo los `undefined`
   * (`exactOptionalPropertyTypes`: no se asigna `undefined` a una clave opcional). */
  function collectErrors(
    fieldErrorsObj: Partial<Record<keyof FieldErrors, string[] | undefined>>,
  ): FieldErrors {
    const errors: FieldErrors = {};
    (Object.keys(fieldErrorsObj) as (keyof FieldErrors)[]).forEach((key) => {
      const message = fieldErrorsObj[key]?.[0];
      if (message) {
        errors[key] = message;
      }
    });
    return errors;
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (tipo === "galv") {
      const result = addGalvSchema.safeParse({ calibre, fmt, kg: cantidad, nota });
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      const body: AddRolloBody = {
        tipo: "galv",
        calibre: result.data.calibre,
        fmt: result.data.fmt,
        rollos: [
          { kg: Number(result.data.kg.replace(",", ".")), nota: result.data.nota },
        ],
      };
      addRollo.mutate(body, { onSuccess: onClose });
    } else {
      const result = addAceroSchema.safeParse({ perfil_id: perfilId, metros: cantidad, nota });
      if (!result.success) {
        setFieldErrors(collectErrors(result.error.flatten().fieldErrors));
        return;
      }
      setFieldErrors({});
      const body: AddRolloBody = {
        tipo: "acero",
        perfil_id: result.data.perfil_id,
        rollos: [
          { metros: Number(result.data.metros.replace(",", ".")), nota: result.data.nota },
        ],
      };
      addRollo.mutate(body, { onSuccess: onClose });
    }
  }

  const apiError = addRollo.isError ? inventarioErrorMessage(addRollo.error) : null;
  const isSubmitting = addRollo.isPending;

  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent>
        <DialogTitle>Agregar lote</DialogTitle>
        <DialogDescription>
          Registra un nuevo lote en la sede activa: galvanizada (por kg) o
          acero/aluminio (por metros).
        </DialogDescription>

        <form className="mt-4 flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          {/* Selector de tipo (galv/acero) */}
          <div
            className="inline-flex w-fit rounded-md border border-slate-300 p-0.5"
            role="group"
            aria-label="Tipo de rollo"
          >
            <button
              type="button"
              aria-pressed={tipo === "galv"}
              onClick={() => setTipo("galv")}
              className={`rounded px-3 py-1 text-sm font-medium ${
                tipo === "galv" ? "bg-brand text-white" : "text-slate-600"
              }`}
            >
              Galvanizada
            </button>
            <button
              type="button"
              aria-pressed={tipo === "acero"}
              onClick={() => setTipo("acero")}
              className={`rounded px-3 py-1 text-sm font-medium ${
                tipo === "acero" ? "bg-brand text-white" : "text-slate-600"
              }`}
            >
              Acero / Aluminio
            </button>
          </div>

          {tipo === "galv" ? (
            <>
              <div className="flex flex-col gap-1">
                <label htmlFor="add-calibre" className="text-sm font-medium text-slate-700">
                  Calibre
                </label>
                <select
                  id="add-calibre"
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  value={calibre}
                  aria-invalid={fieldErrors.calibre ? true : undefined}
                  onChange={(event) => setCalibre(event.target.value)}
                >
                  <option value="">Selecciona…</option>
                  {CALIBRES_GALV.map((c) => (
                    <option key={c.cal} value={c.cal}>
                      {c.label}
                    </option>
                  ))}
                </select>
                {fieldErrors.calibre ? (
                  <p className="text-sm text-red-600">{fieldErrors.calibre}</p>
                ) : null}
              </div>

              <div className="flex flex-col gap-1">
                <label htmlFor="add-fmt" className="text-sm font-medium text-slate-700">
                  Formato
                </label>
                <select
                  id="add-fmt"
                  className="block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  value={fmt}
                  aria-invalid={fieldErrors.fmt ? true : undefined}
                  onChange={(event) => setFmt(event.target.value)}
                >
                  <option value="">Selecciona…</option>
                  {FORMATOS_GALV.map((f) => (
                    <option key={f} value={f}>
                      {f}
                    </option>
                  ))}
                </select>
                {fieldErrors.fmt ? (
                  <p className="text-sm text-red-600">{fieldErrors.fmt}</p>
                ) : null}
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-1">
              <label htmlFor="add-perfil" className="text-sm font-medium text-slate-700">
                Perfil
              </label>
              <select
                id="add-perfil"
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                value={perfilId}
                aria-invalid={fieldErrors.perfil_id ? true : undefined}
                onChange={(event) => setPerfilId(event.target.value)}
              >
                <option value="">Selecciona…</option>
                {PERFILES_ACERO.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              {fieldErrors.perfil_id ? (
                <p className="text-sm text-red-600">{fieldErrors.perfil_id}</p>
              ) : null}
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label htmlFor="add-cantidad" className="text-sm font-medium text-slate-700">
              {tipo === "galv" ? "Kilos (kg)" : "Metros (m)"}
            </label>
            <Input
              id="add-cantidad"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={cantidad}
              aria-invalid={fieldErrors.kg || fieldErrors.metros ? true : undefined}
              onChange={(event) => setCantidad(event.target.value)}
            />
            {fieldErrors.kg || fieldErrors.metros ? (
              <p className="text-sm text-red-600">{fieldErrors.kg ?? fieldErrors.metros}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="add-nota" className="text-sm font-medium text-slate-700">
              Nota (opcional)
            </label>
            <Input
              id="add-nota"
              type="text"
              autoComplete="off"
              value={nota}
              onChange={(event) => setNota(event.target.value)}
            />
          </div>

          {apiError ? (
            <p role="alert" className="text-sm text-red-600">
              {apiError}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Guardando…" : "Agregar lote"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
